import React, { useEffect, useState } from "react";
import axios from "axios";
import { Table, Input, Button } from "reactstrap";
import { toast } from "react-toastify";
import AdminPageShell, { AdminSurface } from "../../../components/Admin/AdminPageShell";
import { getApiUrl } from "../../../utils/site";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("");

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(getApiUrl("/api/users"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data || []);
    } catch (error) {
      toast.error("Failed to fetch users.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (id) => {
    const token = localStorage.getItem("token");
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    try {
      await axios.delete(getApiUrl(`/api/users/${id}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("User deleted successfully.");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to delete user.");
    }
  };

  const handleUnsubscribeUser = async (id) => {
    const token = localStorage.getItem("token");
    if (!window.confirm("Are you sure you want to unsubscribe this user?")) return;

    try {
      await axios.put(
        getApiUrl(`/api/users/${id}/status`),
        { status: "inactive" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("User unsubscribed.");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to unsubscribe user.");
    }
  };

  const filteredUsers = users.filter((user) => {
    const fullName = String(user.full_name || "").toLowerCase();
    const email = String(user.email || "").toLowerCase();
    const matchesSearch =
      fullName.includes(search.toLowerCase()) || email.includes(search.toLowerCase());
    const matchesFilter =
      filter === "" ||
      (filter === "active" && user.status === "active") ||
      (filter === "inactive" && user.status !== "active");
    return matchesSearch && matchesFilter;
  });

  return (
    <AdminPageShell
      eyebrow="Audience management"
      title="Users"
      description="Review account records, subscriber health, and platform roles from one consolidated client management view."
    >
      <AdminSurface title="User directory" subtitle={`${filteredUsers.length} matching records`}>
        <div className="roha-admin-filterbar">
          <Input
            type="search"
            placeholder="Search by name or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Input type="select" value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive or blocked</option>
          </Input>
        </div>

        <div className="roha-admin-table-wrap">
          <Table responsive className="roha-admin-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Full Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Role</th>
                <th>Status</th>
                <th>Subscriber</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="roha-admin-empty">
                    Loading users...
                  </td>
                </tr>
              ) : filteredUsers.length ? (
                filteredUsers.map((user, index) => (
                  <tr key={user.id}>
                    <td data-label="#">{index + 1}</td>
                    <td data-label="Full Name">{user.full_name || "-"}</td>
                    <td data-label="Email">{user.email}</td>
                    <td data-label="Phone">{user.phone_number || user.phone || "-"}</td>
                    <td data-label="Role">{user.role || "-"}</td>
                    <td data-label="Status">
                      <span className="roha-admin-pill">{user.status || "active"}</span>
                    </td>
                    <td data-label="Subscriber">{user.subscriber ? "Yes" : "No"}</td>
                    <td data-label="Created At">{user.created_at ? new Date(user.created_at).toLocaleString() : "-"}</td>
                    <td data-label="Actions">
                      <div className="roha-admin-toolbar">
                        <Button color="danger" size="sm" onClick={() => handleDeleteUser(user.id)}>
                          Delete
                        </Button>
                        {user.subscriber ? (
                          <Button color="warning" size="sm" onClick={() => handleUnsubscribeUser(user.id)}>
                            Unsubscribe
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="roha-admin-empty">
                    No users found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </AdminSurface>
    </AdminPageShell>
  );
};

export default UserManagement;
